import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';

import type {
  ConnectorDto,
  ContactCenterSettings,
  CrmClientMatch,
  CustomerCard,
  CustomerDetails,
  DeliveryStatus,
  DialogPriority,
  LineDetails,
  LineOperator,
  LineSummary,
  Message,
  MessagePage,
  OpenlinesMe,
  OperatorCounters,
  OperatorStatus,
  QuickAnswer,
  SessionDetails,
  SessionListItem,
  SessionView,
  SimulatorAction,
  SimulatorConversation,
  SimulatorInfo,
  SimulatorResult,
  Stats,
  UpdateLineRequest,
} from './types';

/** Query keys (ARCHITECTURE §6.5: `[moduleId, …]`). */
export const openlinesKeys = {
  all: ['openlines'] as const,
  me: () => ['openlines', 'me'] as const,
  counters: () => ['openlines', 'counters'] as const,
  filteredCounters: (filters: SessionFilters) =>
    ['openlines', 'counters', filters.lineId, filters.channel, filters.operatorId] as const,
  sessions: () => ['openlines', 'sessions'] as const,
  sessionList: (view: SessionView, search: string, filters: SessionFilters) =>
    ['openlines', 'sessions', view, search, filters.lineId, filters.channel, filters.operatorId] as const,
  crmMatches: (sessionId: string) => ['openlines', 'session', sessionId, 'crm-matches'] as const,
  session: (id: string) => ['openlines', 'session', id] as const,
  messages: (id: string) => ['openlines', 'messages', id] as const,
  customer: (id: string) => ['openlines', 'customer', id] as const,
  lines: () => ['openlines', 'lines'] as const,
  line: (id: string) => ['openlines', 'line', id] as const,
  operators: (id: string) => ['openlines', 'line', id, 'operators'] as const,
  connectors: (id: string) => ['openlines', 'line', id, 'connectors'] as const,
  quickAnswers: (lineId: string | null) => ['openlines', 'quick-answers', lineId ?? 'all'] as const,
  stats: (from: string, to: string, lineId: string) => ['openlines', 'stats', from, to, lineId] as const,
  simulator: () => ['openlines', 'simulator'] as const,
  simulatorConversation: (connectorId: string, externalId: string) =>
    ['openlines', 'simulator', 'conversation', connectorId, externalId] as const,
  settings: () => ['openlines', 'settings'] as const,
};

const base = '/openlines';
const sessionPath = (id: string) => `${base}/sessions/${encodeURIComponent(id)}`;

/** The filters of the dialog list (empty: all); supervisors narrow it to a line, a channel or an operator. */
export interface SessionFilters {
  lineId: string;
  channel: string;
  operatorId: string;
}

export const NO_FILTERS: SessionFilters = { lineId: '', channel: '', operatorId: '' };

/** The first page of a list (no cursor). */
const NO_CURSOR: { after?: string; afterId?: string } = {};

/** Rows of the dialog list per page (the next page follows the last row: `after` + `afterId`). */
export const SESSION_PAGE_SIZE = 50;

/** Where the next page starts: the last row's sort value in the view (closed: the closing time; queue: creation). */
export function sessionCursor(view: SessionView, last: SessionListItem): { after: string; afterId: string } {
  const after =
    view === 'queue' ? last.createdAt : view === 'closed' ? (last.closedAt ?? last.lastMessageAt) : last.lastMessageAt;
  return { after, afterId: last.id };
}

export const fetchSessions = (
  view: SessionView,
  search: string,
  filters: SessionFilters = NO_FILTERS,
  options: { limit?: number; after?: string; afterId?: string; signal?: AbortSignal } = {},
) =>
  api.get<SessionListItem[]>(`${base}/sessions`, {
    query: {
      view,
      search: search || undefined,
      lineId: filters.lineId || undefined,
      channel: filters.channel || undefined,
      operatorId: filters.operatorId || undefined,
      limit: options.limit ?? SESSION_PAGE_SIZE,
      after: options.after,
      afterId: options.afterId,
    },
    signal: options.signal,
  });

export const markSessionRead = (id: string, seq: number) =>
  api.post<{ lastReadSeq: number }>(`${sessionPath(id)}/read`, { seq });

export const sendOperatorTyping = (id: string) => api.post(`${sessionPath(id)}/typing`);

export function useOpenlinesMe(enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.me(),
    queryFn: ({ signal }) => api.get<OpenlinesMe>(`${base}/me`, { signal }),
    staleTime: 60_000,
    enabled,
  });
}

export function useCounters(enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.counters(),
    queryFn: ({ signal }) => api.get<OperatorCounters>(`${base}/counters`, { signal }),
    staleTime: 15_000,
    enabled,
  });
}

/** The counters of the dialog list's tabs for its filters (the plain counters when nothing is filtered). */
export function useFilteredCounters(filters: SessionFilters) {
  const filtered = !!(filters.lineId || filters.channel || filters.operatorId);
  return useQuery({
    queryKey: filtered ? openlinesKeys.filteredCounters(filters) : openlinesKeys.counters(),
    queryFn: ({ signal }) =>
      api.get<OperatorCounters>(`${base}/counters`, {
        query: {
          lineId: filters.lineId || undefined,
          channel: filters.channel || undefined,
          operatorId: filters.operatorId || undefined,
        },
        signal,
      }),
    staleTime: 15_000,
  });
}

/**
 * A view of the dialog list in pages of {@link SESSION_PAGE_SIZE}: the next page follows the last row (keyset), so the
 * closed history stays reachable however long it grows.
 */
export function useSessions(view: SessionView, search: string, filters: SessionFilters = NO_FILTERS) {
  return useInfiniteQuery({
    queryKey: openlinesKeys.sessionList(view, search, filters),
    queryFn: ({ pageParam, signal }) => fetchSessions(view, search, filters, { ...pageParam, signal }),
    initialPageParam: NO_CURSOR,
    getNextPageParam: (page) => {
      const last = page.at(-1);
      return page.length < SESSION_PAGE_SIZE || !last ? undefined : sessionCursor(view, last);
    },
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

/** The rows of the loaded pages (a row that moved between pages while they loaded shows once). */
export function flattenSessions(data: InfiniteData<SessionListItem[]> | undefined): SessionListItem[] {
  if (!data) return [];
  const seen = new Set<string>();
  return data.pages.flat().filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true)));
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: openlinesKeys.session(id ?? ''),
    queryFn: ({ signal }) => api.get<SessionDetails>(sessionPath(id ?? ''), { signal }),
    enabled: !!id,
    retry: false,
  });
}

/** The history of a dialog: pages of 50, older ones loaded upwards (`before` = the oldest loaded number). */
export function useMessages(id: string) {
  return useInfiniteQuery({
    queryKey: openlinesKeys.messages(id),
    queryFn: ({ pageParam, signal }) =>
      api.get<MessagePage>(`${sessionPath(id)}/messages`, {
        query: { before: pageParam ?? undefined, limit: 50 },
        signal,
      }),
    initialPageParam: null as number | null,
    getNextPageParam: (page) => page.olderCursor,
    retry: false,
  });
}

export type MessagesData = InfiniteData<MessagePage>;

/** All loaded messages, oldest first (pages are newest first). */
export function flattenMessages(data: MessagesData | undefined): Message[] {
  if (!data) return [];
  const all = [...data.pages].reverse().flatMap((page) => page.items);
  const seen = new Set<string>();
  return all.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
}

const DELIVERY_ORDER: Record<DeliveryStatus, number> = {
  none: 0,
  pending: 1,
  sent: 2,
  delivered: 3,
  read: 4,
  failed: 5,
};

/**
 * Whether `incoming` is an older state of a message than `stored`: delivery only moves forward (pending, sent,
 * delivered, read), as on the server (`SessionMessage.SetDelivery`); a failure is always news and a failed message may
 * be sent again.
 */
export function isOlderDelivery(stored: DeliveryStatus, incoming: DeliveryStatus): boolean {
  return incoming !== 'failed' && stored !== 'failed' && DELIVERY_ORDER[incoming] < DELIVERY_ORDER[stored];
}

/**
 * Inserts or replaces a message in the loaded history (realtime, optimistic sending). Updates of one message can arrive
 * out of order — the answer of the send request after the realtime update of a fast channel, the "sent" event after the
 * "delivered" one when the relay and the fan-out handled them side by side — and the older one must not undo the newer:
 * a stored copy that moved on (sent, delivered…) keeps its delivery state.
 */
export function upsertMessage(queryClient: QueryClient, message: Message): void {
  queryClient.setQueryData<MessagesData>(openlinesKeys.messages(message.sessionId), (data) => {
    if (!data || data.pages.length === 0) return data;
    const stored = data.pages.flatMap((page) => page.items).find((m) => m.id === message.id && !m.pending);
    if (stored && isOlderDelivery(stored.delivery, message.delivery)) {
      message = { ...message, delivery: stored.delivery, deliveryError: stored.deliveryError };
    }
    const exists = data.pages.some((page) => page.items.some((m) => m.id === message.id));
    // The optimistic copy (same client id) gives way to the stored message.
    const pages = data.pages.map((page) => ({
      ...page,
      items: page.items
        .filter(
          (m) => !(message.clientId && m.pending && m.clientId === message.clientId && m.id !== message.id),
        )
        .map((m) => (m.id === message.id ? message : m)),
    }));
    if (exists) return { ...data, pages };
    const [newest, ...rest] = pages;
    if (!newest) return data;
    const items = [...newest.items, message].sort((a, b) => order(a) - order(b));
    return { ...data, pages: [{ ...newest, items }, ...rest] };
  });
}

/** Drops a message from the loaded history (a failed optimistic copy that is sent again). */
export function removeMessage(queryClient: QueryClient, sessionId: string, messageId: string): void {
  queryClient.setQueryData<MessagesData>(openlinesKeys.messages(sessionId), (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({ ...page, items: page.items.filter((m) => m.id !== messageId) })),
        }
      : data,
  );
}

/** Optimistic messages sort after every stored one. */
const order = (m: Message) => (m.pending ? Number.MAX_SAFE_INTEGER - 1 : m.seq);

/** Delivery of the operator's messages up to the client's acknowledgements (the widget's delivered / read). */
export function applyCustomerAcks(
  queryClient: QueryClient,
  sessionId: string,
  delivered: number,
  read: number,
): void {
  queryClient.setQueryData<MessagesData>(openlinesKeys.messages(sessionId), (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((m) => {
              if (m.kind !== 'text' || (m.author !== 'operator' && m.author !== 'bot') || m.pending) return m;
              if (m.seq <= read && m.delivery !== 'read' && m.delivery !== 'failed')
                return { ...m, delivery: 'read' as const };
              if (m.seq <= delivered && (m.delivery === 'pending' || m.delivery === 'sent'))
                return { ...m, delivery: 'delivered' as const };
              return m;
            }),
          })),
        }
      : data,
  );
}

export function useSendMessage(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { text: string; attachmentIds: string[]; clientId: string; note: boolean }) =>
      api.post<Message>(`${sessionPath(sessionId)}/messages`, {
        text: input.text,
        attachmentIds: input.attachmentIds,
        clientId: input.clientId,
        note: input.note,
      }),
    onSuccess: (message) => {
      upsertMessage(queryClient, message);
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.session(sessionId) });
    },
    meta: { silent: true },
  });
}

function useSessionAction<TBody = void>(sessionId: string, path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      api.post<SessionDetails>(`${sessionPath(sessionId)}/${path}`, body ?? undefined),
    onSuccess: (session) => {
      queryClient.setQueryData(openlinesKeys.session(sessionId), session);
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.sessions() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.counters() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.messages(sessionId) });
    },
  });
}

/** Sets the tags and the priority of a dialog (`null` leaves one unchanged). */
export function useLabelSession(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { tags?: string[] | null; priority?: DialogPriority | null }) =>
      api.put<SessionDetails>(`${sessionPath(sessionId)}/labels`, body),
    onSuccess: (session) => {
      queryClient.setQueryData(openlinesKeys.session(sessionId), session);
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.sessions() });
    },
  });
}

export const useTake = (id: string) => useSessionAction(id, 'take');
export const useSkip = (id: string) => useSessionAction(id, 'skip');
export const useLinkCrm = (id: string) =>
  useSessionAction<{ entityType: 'lead' | 'deal' | 'contact' | 'company'; entityId: string }>(id, 'crm/link');

/** CRM records that may be the dialog's client (by phone, e-mail, messenger account), for the operator to link. */
export function useCrmMatches(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: openlinesKeys.crmMatches(sessionId),
    queryFn: ({ signal }) => api.get<CrmClientMatch>(`${sessionPath(sessionId)}/crm/matches`, { signal }),
    enabled,
    staleTime: 30_000,
  });
}

/** An operator corrects the client's name, phone and e-mail. */
export function useUpdateCustomer(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { customerId: string; name: string | null; phone: string | null; email: string | null }) =>
      api.put<CustomerDetails>(`${base}/customers/${encodeURIComponent(input.customerId)}`, {
        name: input.name,
        phone: input.phone,
        email: input.email,
      }),
    onSuccess: (customer) => {
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.session(sessionId) });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.customer(customer.id) });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.sessions() });
    },
    meta: { silent: true },
  });
}
export const useClose = (id: string) => useSessionAction(id, 'close');
export const useSpam = (id: string) => useSessionAction(id, 'spam');
export const useReopen = (id: string) => useSessionAction(id, 'reopen');
export const useSendToCrm = (id: string) => useSessionAction(id, 'crm');
export const useTransfer = (id: string) =>
  useSessionAction<{ operatorId?: string | null; lineId?: string | null }>(id, 'transfer');

export function useRetryMessage(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.post<Message>(`${sessionPath(sessionId)}/messages/${encodeURIComponent(messageId)}/retry`),
    onSuccess: (message) => upsertMessage(queryClient, message),
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: openlinesKeys.customer(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<CustomerCard>(`${base}/customers/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: !!id,
    retry: false,
  });
}

export function useUnblockCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`${base}/customers/${encodeURIComponent(id)}/unblock`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: openlinesKeys.all }),
  });
}

export function useSetStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: OperatorStatus) => api.put<OpenlinesMe>(`${base}/me/status`, { status }),
    onSuccess: (me) => queryClient.setQueryData(openlinesKeys.me(), me),
  });
}

export function useLines(enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.lines(),
    queryFn: ({ signal }) => api.get<LineSummary[]>(`${base}/lines`, { signal }),
    enabled,
  });
}

export function useLine(id: string | undefined) {
  return useQuery({
    queryKey: openlinesKeys.line(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<LineDetails>(`${base}/lines/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: !!id,
    retry: false,
  });
}

export function useCreateLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<LineDetails>(`${base}/lines`, { name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.me() });
    },
  });
}

export function useUpdateLine(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateLineRequest) =>
      api.put<LineDetails>(`${base}/lines/${encodeURIComponent(id)}`, request),
    onSuccess: (line) => {
      queryClient.setQueryData(openlinesKeys.line(id), line);
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.me() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.operators(id) });
    },
    meta: { silent: true },
  });
}

export function useDeleteLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${base}/lines/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.me() });
    },
  });
}

export function useLineOperators(id: string, enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.operators(id),
    queryFn: ({ signal }) =>
      api.get<LineOperator[]>(`${base}/lines/${encodeURIComponent(id)}/operators`, { signal }),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useConnectors(lineId: string) {
  return useQuery({
    queryKey: openlinesKeys.connectors(lineId),
    queryFn: ({ signal }) =>
      api.get<ConnectorDto[]>(`${base}/lines/${encodeURIComponent(lineId)}/connectors`, { signal }),
  });
}

export function useConfigureConnector(lineId: string, type: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: { enabled: boolean; settings: unknown; secret?: string | null }) =>
      api.put<ConnectorDto>(
        `${base}/lines/${encodeURIComponent(lineId)}/connectors/${encodeURIComponent(type)}`,
        request,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.connectors(lineId) });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
    },
    meta: { silent: true },
  });
}

export function useCheckConnector(lineId: string, type: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ConnectorDto>(
        `${base}/lines/${encodeURIComponent(lineId)}/connectors/${encodeURIComponent(type)}/check`,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: openlinesKeys.connectors(lineId) }),
  });
}

export function useQuickAnswers(lineId: string | null, enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.quickAnswers(lineId),
    queryFn: ({ signal }) =>
      api.get<QuickAnswer[]>(`${base}/quick-answers`, { query: { lineId: lineId ?? undefined }, signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveQuickAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      lineId: string | null;
      title: string;
      text: string;
      sortOrder?: number | null;
    }) =>
      input.id
        ? api.put<QuickAnswer>(`${base}/quick-answers/${encodeURIComponent(input.id)}`, input)
        : api.post<QuickAnswer>(`${base}/quick-answers`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['openlines', 'quick-answers'] }),
    meta: { silent: true },
  });
}

export function useDeleteQuickAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${base}/quick-answers/${encodeURIComponent(id)}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['openlines', 'quick-answers'] }),
  });
}

export function useStats(from: string, to: string, lineId: string) {
  return useQuery({
    queryKey: openlinesKeys.stats(from, to, lineId),
    queryFn: ({ signal }) =>
      api.get<Stats>(`${base}/stats`, { query: { from, to, lineId: lineId || undefined }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useSimulator(enabled: boolean) {
  return useQuery({
    queryKey: openlinesKeys.simulator(),
    queryFn: ({ signal }) => api.get<SimulatorInfo>(`${base}/dev/simulator`, { signal }),
    enabled,
    retry: false,
  });
}

export function useSimulate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectorId: string;
      externalId: string | null;
      name: string | null;
      phone: string | null;
      email: string | null;
      username: string | null;
      text: string | null;
      fileId?: string | null;
      shareContact?: boolean;
    }) => api.post<SimulatorResult>(`${base}/dev/simulator/messages`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: openlinesKeys.simulator() }),
  });
}

/** The fake client's chat, refreshed every 1.5 s while it is shown (the line answers, statuses change). */
export function useSimulatorConversation(connectorId: string | undefined, externalId: string | undefined) {
  return useQuery({
    queryKey: openlinesKeys.simulatorConversation(connectorId ?? '', externalId ?? ''),
    queryFn: ({ signal }) =>
      api.get<SimulatorConversation>(`${base}/dev/simulator/conversation`, {
        query: { connectorId, externalId },
        signal,
      }),
    enabled: !!connectorId && !!externalId,
    refetchInterval: 1500,
    retry: false,
  });
}

export function useSimulatorAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectorId: string;
      externalId: string;
      action: SimulatorAction;
      value?: number;
    }) => api.post(`${base}/dev/simulator/actions`, input),
    onSuccess: (_, input) =>
      void queryClient.invalidateQueries({
        queryKey: openlinesKeys.simulatorConversation(input.connectorId, input.externalId),
      }),
  });
}

export function useContactCenterSettings(enabled = true) {
  return useQuery({
    queryKey: openlinesKeys.settings(),
    queryFn: ({ signal }) => api.get<ContactCenterSettings>(`${base}/settings`, { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateContactCenterSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { publicBaseUrl: string | null }) =>
      api.put<ContactCenterSettings>(`${base}/settings`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(openlinesKeys.settings(), saved);
      void queryClient.invalidateQueries({ queryKey: ['openlines', 'line'] });
    },
    meta: { silent: true },
  });
}

/** A step of signing a line's Telegram account in or out. */
export type TelegramLoginStep =
  | { step: 'start'; mode: 'live' | 'emulated'; apiId: number | null; apiHash: string | null; phone: string }
  | { step: 'code'; code: string }
  | { step: 'password'; password: string }
  | { step: 'cancel' }
  | { step: 'logout' };

function telegramLoginRequest(root: string, input: TelegramLoginStep) {
  if (input.step === 'start') {
    const { mode, apiId, apiHash, phone } = input;
    return api.post<ConnectorDto>(`${root}/login`, { mode, apiId, apiHash, phone });
  }
  if (input.step === 'code') return api.post<ConnectorDto>(`${root}/login/code`, { code: input.code });
  if (input.step === 'password')
    return api.post<ConnectorDto>(`${root}/login/password`, { password: input.password });
  if (input.step === 'cancel') return api.delete<ConnectorDto>(`${root}/login`);
  return api.post<ConnectorDto>(`${root}/logout`);
}

/** The sign-in steps of a line's Telegram account (each answers the connector's new state). */
export function useTelegramLogin(lineId: string) {
  const queryClient = useQueryClient();
  const root = `${base}/lines/${encodeURIComponent(lineId)}/connectors/telegram-user`;
  return useMutation({
    mutationFn: (input: TelegramLoginStep) => telegramLoginRequest(root, input),
    onSuccess: (connector) => {
      queryClient.setQueryData<ConnectorDto[]>(openlinesKeys.connectors(lineId), (list) =>
        list?.map((c) => (c.type === connector.type ? connector : c)),
      );
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.connectors(lineId) });
      void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
    },
    meta: { silent: true },
  });
}
